import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Users, Plus, Trash2, ChevronDown, ChevronRight, X, Pencil, Check } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import TeamMembersPanel from '../../components/admin/TeamMembersPanel';
import { adminApi } from '../../api/admin';
import api from '../../api/client';

/**
 * TeamsManager — admin page for tenant team CRUD (Teams Part 3A).
 *
 * Tenant admins (is_admin=true) can create and delete teams. Team admins
 * (is_team_admin=true on a specific team) can rename their team and manage
 * its roster but cannot create new teams or delete existing ones — the
 * backend enforces this via requirePermission + scoped grants.
 *
 * The frontend's "show button or hide it" logic uses `is_admin` only — it
 * doesn't model the per-team scoped grants because the backend enforces
 * them anyway. Team admins see the same buttons; the backend rejects the
 * unauthorized ones with 403. Source of truth is the server.
 *
 * The General team (is_default=true) can be renamed but cannot be deleted.
 * The canonical identifier is is_default=true, never the literal string
 * 'General'.
 */
export default function TeamsManager() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newTeam, setNewTeam] = useState({ name: '', description: '', color: '#3B82F6' });
  const [deleteRefs, setDeleteRefs] = useState(null);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.listTeams();
      setTeams(res.data?.teams || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setCurrentUser(res.data?.user || res.data || null);
    } catch (err) {
      // Non-fatal — just disables admin-only buttons
    }
  }, []);

  useEffect(() => {
    loadTeams();
    loadMe();
  }, [loadTeams, loadMe]);

  const isTenantAdmin = currentUser?.is_admin === true;

  const handleCreate = async () => {
    if (!newTeam.name.trim()) {
      toast.error('Team name required');
      return;
    }
    try {
      await adminApi.createTeam({
        name: newTeam.name.trim(),
        description: newTeam.description || null,
        color: newTeam.color || null,
      });
      toast.success('Team created');
      setShowCreate(false);
      setNewTeam({ name: '', description: '', color: '#3B82F6' });
      loadTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create team');
    }
  };

  const startEdit = (team) => {
    setEditingId(team.id);
    setEditName(team.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = async (team) => {
    if (!editName.trim() || editName === team.name) {
      cancelEdit();
      return;
    }
    try {
      await adminApi.updateTeam(team.id, { name: editName.trim() });
      toast.success('Team renamed');
      cancelEdit();
      loadTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to rename team');
    }
  };

  const handleDeleteClick = async (team) => {
    if (team.is_default) {
      toast.error('The default General team cannot be deleted');
      return;
    }
    try {
      // Pre-check references so we can show a useful error before the destructive action
      const res = await adminApi.getTeamReferences(team.id);
      const refs = res.data || {};
      const hasRefs = (refs.stages_owned?.length || 0) > 0
        || (refs.records_primary || 0) > 0
        || (refs.visibility_rules?.length || 0) > 0;
      if (hasRefs) {
        setDeleteRefs({ team, refs });
        return;
      }
      if (!window.confirm(`Delete team "${team.name}"? This cannot be undone.`)) return;
      await adminApi.deleteTeam(team.id);
      toast.success('Team deleted');
      loadTeams();
    } catch (err) {
      // Backend may also return 409 with references if a race created new refs after the precheck
      if (err.response?.status === 409 && err.response?.data?.references) {
        setDeleteRefs({ team, refs: err.response.data.references });
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete team');
      }
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <AdminPageHeader
        title="Teams"
        description="Manage teams, their members, and which workflow stages each team owns. Tenant admins can create and delete teams; team admins manage their own team's roster."
        icon={Users}
        iconColor="text-indigo-600"
        iconBg="bg-indigo-50"
      >
        {isTenantAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" />
            Create Team
          </button>
        )}
      </AdminPageHeader>

      <div className="px-6 pb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left w-10"></th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-center">Members</th>
                <th className="px-4 py-3 text-center">Stages Owned</th>
                <th className="px-4 py-3 text-center">Records (Primary)</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teams.map((t) => (
                <React.Fragment key={t.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleExpand(t.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expandedId === t.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {editingId === t.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit(t);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            className="px-2 py-1 border border-brand-500 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                            ref={(el) => { if (el) el.focus(); }}
                          />
                          <button onClick={() => saveEdit(t)} className="text-green-600 hover:text-green-700">
                            <Check className="h-4 w-4" />
                          </button>
                          <button onClick={() => cancelEdit()} className="text-gray-400 hover:text-gray-600">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: t.color || '#3B82F6' }}
                          />
                          <span className="font-medium text-gray-900">{t.name}</span>
                          {t.is_default && (
                            <span className="px-1.5 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded">
                              Default
                            </span>
                          )}
                          <button
                            onClick={() => startEdit(t)}
                            className="text-gray-300 hover:text-gray-600 ml-1"
                            title="Rename"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      {t.description && editingId !== t.id && (
                        <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{t.member_count}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{t.stage_count}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{t.primary_record_count}</td>
                    <td className="px-4 py-3 text-right">
                      {!t.is_default && (
                        <button
                          onClick={() => handleDeleteClick(t)}
                          className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                          title="Delete team"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === t.id && (
                    <tr>
                      <td colSpan="6" className="p-0">
                        <TeamMembersPanel team={t} currentUser={currentUser} onChange={() => loadTeams()} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {teams.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-500 italic">
                    No teams yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create Team</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="e.g. Pre-Litigation"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newTeam.description}
                  onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="color"
                  value={newTeam.color}
                  onChange={(e) => setNewTeam({ ...newTeam, color: e.target.value })}
                  className="h-10 w-20 border border-gray-300 rounded-lg cursor-pointer"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCreate()}
                disabled={!newTeam.name.trim()}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete blocked-by-references modal */}
      {deleteRefs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Cannot delete {deleteRefs.team.name}</h2>
              <button onClick={() => setDeleteRefs(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              <p className="text-gray-700">This team has active references. Reassign or remove them first.</p>
              {deleteRefs.refs.stages_owned?.length > 0 && (
                <div>
                  <div className="font-medium text-gray-900">Workflow stages owned ({deleteRefs.refs.stages_owned.length})</div>
                  <ul className="mt-1 ml-4 list-disc text-gray-600 text-xs">
                    {deleteRefs.refs.stages_owned.map((s) => (
                      <li key={s.id}>{s.workflow_name} → {s.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {deleteRefs.refs.records_primary > 0 && (
                <div>
                  <div className="font-medium text-gray-900">Records currently primary-owned</div>
                  <div className="text-xs text-gray-600 mt-1">{deleteRefs.refs.records_primary} record(s). Transition them to a stage owned by another team first.</div>
                </div>
              )}
              {deleteRefs.refs.visibility_rules?.length > 0 && (
                <div>
                  <div className="font-medium text-gray-900">Field visibility rules ({deleteRefs.refs.visibility_rules.length})</div>
                  <ul className="mt-1 ml-4 list-disc text-gray-600 text-xs">
                    {deleteRefs.refs.visibility_rules.map((v) => (
                      <li key={v.field_id}>{v.record_type} → {v.field_label}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setDeleteRefs(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
